// The shape of a call, shared by the server (what Santa is told) and the browser (what the cabin
// shows). Each call picks an opening, two surprises and a goodbye at random, so no two calls
// feel the same. Santa's directions are in English; he speaks the call's language.

export type OpeningId = 'fireside' | 'stable' | 'nap' | 'elf';
export type EventId = 'rudolph' | 'cat' | 'fire' | 'aurora';

export const OPENINGS: Record<OpeningId, { speaker: 'mrsClaus' | 'elf'; santa: string }> = {
  fireside: {
    speaker: 'mrsClaus',
    santa: 'Mrs. Claus answered the phone and called you over; you were reading by the fire in your armchair. Take the phone from her with a big warm hello.',
  },
  stable: {
    speaker: 'mrsClaus',
    santa: 'You have JUST come in from feeding the reindeer: a little out of breath, snow on your hat and shoulders, cold hands. Take the phone from Mrs. Claus, puffing happily, and mention where you just were.',
  },
  nap: {
    speaker: 'mrsClaus',
    santa: 'You had dozed off in your rocking chair and Mrs. Claus just woke you up to take the phone. Start with a sleepy snort, then a chuckle: you were NOT sleeping, you were only resting your eyes. Then wake up properly, delighted.',
  },
  elf: {
    speaker: 'elf',
    santa: 'Pipa, the elf who runs the North Pole telephone switchboard, just put the call through to you. Thank Pipa, then greet the child.',
  },
};

export const EVENTS: Record<EventId, string> = {
  rudolph: 'Rudolph is peeking in through the window right now, his red nose glowing. Mention it briefly when it fits, and maybe let him say hello.',
  cat: 'Pipari the cat just woke up by the fire, gave a big yawn and looked at you. Mention it briefly ("look who\'s awake!") when it fits.',
  fire: 'A log in the fireplace just popped with a shower of sparks. React with a chuckle in a few words when it fits.',
  aurora: 'The northern lights outside the window just flared up bright green and pink. Mention it with wonder when it fits.',
};

/** The one-minute warning for Santa. */
export const SANTA_WRAP_UP = '[ELVES: About one minute left. Start steering gently towards the end: no new topics. Do NOT say goodbye yet; the elves will tell you when.]';

/** Said a goodbye too early? The elves nudge them to carry on, playfully. */
export const NOT_YET = "[ELVES: The switchboard says there is still time! Say something playful like 'oh wait, the elves say I still have a little time!' and carry on.]";

/** Recognises a goodbye in what the character just said. */
export const SAYS_GOODBYE = /\b(good-?bye|bye-bye|bye now|merry christmas,? (and )?good|have to go now|must go now)\b/i;

export const GOODBYES = [
  'the reindeer need their supper',
  'Mrs. Claus says the rice porridge is ready',
  'the elves need you to test a new toy in the workshop',
  'it is time for the evening sleigh practice',
];

export function pick<T>(list: readonly T[]): T {
  return list[Math.floor(Math.random() * list.length)];
}

/**
 * Santa's show: like a Santa on a home visit, he always knows what comes next. Little children
 * often answer with one word or not at all, so he leads, mixing things to listen to (a tiny story,
 * his song) with things to do (a game, a magic moment, an easy question).
 */
export const SEGMENTS = {
  'story-rudolph': 'Tell a tiny story from today (four or five sentences, with a hush before the funny bit): at flying practice Rudolph sneezed so hard that all the snow slid off the stable roof, right onto Mrs. Claus, who laughed and made him carry the gingerbread home. Then ask if they have ever done a really big sneeze.',
  'story-cat': 'Tell a tiny story from last night (four or five sentences): Pipari the cat found a ball of red yarn in the workshop and chased it round and round the sleigh, and in the morning the whole sleigh was wrapped up like a present. Then ask if they have a pet, or which animal they would like to have.',
  'story-snowman': 'Tell a tiny story (four or five sentences): the elves built a snowman who "borrowed" your spare hat, and every morning he seems to be standing somewhere new: by the stable, by the sauna, once even at the kitchen window. Then ask what they would call a snowman.',
  'story-elf': 'Tell a tiny story (four or five sentences): the smallest elf wrapped a present so carefully that he wrapped himself up inside it, and the others only found him because the parcel was giggling. Then ask if they like wrapping presents.',
  song: 'Sing your little song (the exact words under YOUR SONG), slowly and warmly, like a grandfather humming by the fire. Then ask if they would like to sing it with you, or if they know a Christmas song they could sing for you. Listen happily to anything they sing.',
  'game-reindeer': 'Play the reindeer-name game: say the start of a reindeer name ("Ru... Ru...") and let the child guess (Rudolph!). Give lots of help and cheer every guess. Two or three rounds.',
  'game-sounds': 'Play the sounds game: ask the child to make the sound of a reindeer, then of a cat, then of a snowman (that one is silly: snowmen are very quiet!). React to each with delight.',
  'game-sack': 'Play the present-riddle game: describe a present in your sack only by its shape and sound ("it is long and round, and it goes toot-toot!") and let the child guess. It is for another child far away, so it is not a promise. Two riddles.',
  'magic-fire': 'A magic moment: ask the child to blow at the screen to make your fire burn brighter. Count "one, two, three, BLOW!", then be amazed at how it crackled and warmed your toes.',
  'magic-window': 'A magic moment: ask the child to wave at your window, because Rudolph might be outside. Then tell them his nose glowed brighter: he waved back with his antlers.',
  'ask-tree': 'Ask an easy question about Christmas at their home: do they have a Christmas tree yet, and what goes on the top? Then tell them about the straw stars on your tree.',
  'ask-either': 'Ask a fun this-or-that question: snow or sunshine? gingerbread or porridge? reindeer or elves? After they answer, tell your own answer with a funny little reason.',
} as const;
export type SegmentId = keyof typeof SEGMENTS;

const STORIES: SegmentId[] = ['story-rudolph', 'story-cat', 'story-snowman', 'story-elf'];
const DOINGS: SegmentId[] = ['game-reindeer', 'game-sounds', 'game-sack', 'magic-fire', 'magic-window'];
const ASKS: SegmentId[] = ['ask-tree', 'ask-either'];
const shuffle = <T>(l: readonly T[]) => [...l].sort(() => Math.random() - 0.5);

/** A call's random plan: opening, two different surprises, goodbye and the running order of the show. */
export function planCall() {
  const events = (Object.keys(EVENTS) as EventId[]).sort(() => Math.random() - 0.5).slice(0, 2);
  // Listen, do, listen, do...: two stories and the song between games, magic and easy questions.
  const listen = [...shuffle(STORIES).slice(0, 2), 'song' as SegmentId].sort(() => Math.random() - 0.5);
  const doing = [...shuffle(DOINGS).slice(0, 2), pick(ASKS)].sort(() => Math.random() - 0.5);
  const show = listen.flatMap((l, i) => [doing[i], l]);
  return { opening: pick(Object.keys(OPENINGS) as OpeningId[]), events, goodbye: pick(GOODBYES), show };
}

/** Halfway through, if the song has not come up yet: it is a highlight, so it should not be skipped. */
export const SONG_CUE = '[ELVES: When you have answered the child, this is a lovely moment for your little song (YOUR SONG), unless you have already sung it. Then carry on.]';

/**
 * The child has gone quiet: the elves tell Santa to lead. After two quiet spells in a row, something
 * to listen to works better than another question.
 */
export function quietNudge(name: string, next: SegmentId | null, again: boolean) {
  const what = next ? `Go on with the next part of your show that you have not done yet, for example: ${SEGMENTS[next]}` : 'Tell another tiny story about your day at the North Pole, then ask an easy yes-or-no question.';
  return `[ELVES: ${name} has gone quiet${again ? ' again' : ''}. That is fine: little ones often do. Do not wait and never ask if they are still there; lead the show now, warmly.${again ? ' Choose something they can just listen to (a tiny story or your song).' : ''} ${what}]`;
}
export type CallPlan = ReturnType<typeof planCall>;

/** What Santa hears when the child taps something in his cabin. */
export const TAPS: Record<string, string> = {
  cat: 'The child just tapped Pipari the cat, who woke up, yawned and looked at you.',
  rudolph: 'The child just tapped the window and Rudolph peeked in, his red nose glowing.',
  fire: 'The child just tapped the fire and a log popped with a shower of sparks.',
  radio: 'The child just tapped your old radio and it played a little Jingle Bells tune.',
  lantern: 'The child tapped the lantern on the table and its flame flared up.',
  mug: 'The child tapped your cup of cocoa and it steamed up.',
  tree: 'The child tapped the Christmas tree and its candles twinkled one after another.',
  santa: 'The child just poked you through the screen, and it tickled!',
};
